import { useEffect, useState, useCallback } from "react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json") ? await res.json().catch(() => ({})) : await res.text();
  if (!res.ok) {
    const msg = (data && data.detail) || data || "Request failed";
    throw new Error(typeof msg === "string" ? msg : "Request failed");
  }
  return data;
}

export function resolveImageUrl(ref) {
  if (!ref) return "";
  if (typeof ref === "string") {
    if (ref.startsWith("http") || ref.startsWith("data:")) return ref;
    if (ref.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${ref}`;
    return ref;
  }
  if (ref.url) return resolveImageUrl(ref.url);
  if (ref.path) return `${process.env.REACT_APP_BACKEND_URL}/api/files/${ref.path}`;
  return "";
}

export function mainImage(product) {
  if (!product?.images?.length) return "";
  return resolveImageUrl(product.images[0]);
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch("/auth/me");
      setUser(data?.authenticated ? data.user : null);
    } catch { setUser(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    refresh();
  }, [refresh]);
  const logout = async () => {
    try { await apiFetch("/auth/logout", { method: "POST" }); } catch {}
    setUser(null);
  };
  return { user, setUser, loading, refresh, logout };
}

export function useCart() {
  const [cart, setCart] = useState(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem("gmk-cart") || "[]"
      );

      if (!Array.isArray(saved)) return [];

      return saved.map((item) => ({
        ...item,
        quantity: Math.max(1, Number(item.quantity) || 1),
        stock:
          item.stock === null ||
          item.stock === undefined ||
          item.stock === ""
            ? null
            : Math.max(0, Number(item.stock) || 0),
      }));
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("gmk-cart", JSON.stringify(cart));
  }, [cart]);

  const addItem = (p, qty = 1) => {
    if (!p?.id) return;

    const requestedQty = Math.max(1, Number(qty) || 1);

    const rawStock = p.stock;
    const stock =
      rawStock === null ||
      rawStock === undefined ||
      rawStock === ""
        ? null
        : Math.max(0, Number(rawStock) || 0);

    // A known stock value of 0 means the product cannot be added.
    if (stock !== null && stock <= 0) {
      return;
    }

    setCart((c) => {
      const idx = c.findIndex((x) => x.id === p.id);

      if (idx >= 0) {
        const next = [...c];
        const existing = next[idx];

        const existingQuantity = Math.max(
          1,
          Number(existing.quantity) || 1
        );

        const existingStock =
          stock !== null
            ? stock
            : existing.stock === null ||
              existing.stock === undefined ||
              existing.stock === ""
              ? null
              : Math.max(0, Number(existing.stock) || 0);

        const maximum =
          existingStock !== null
            ? existingStock
            : Infinity;

        const newQuantity = Math.min(
          existingQuantity + requestedQty,
          maximum
        );

        next[idx] = {
          ...existing,
          name: p.name,
          price: p.price,
          slug: p.slug,
          images: p.images,
          stock: existingStock,
          quantity: newQuantity,
        };

        return next;
      }

      const quantity =
        stock !== null
          ? Math.min(requestedQty, stock)
          : requestedQty;

      if (quantity <= 0) {
        return c;
      }

      return [
        ...c,
        {
          id: p.id,
          name: p.name,
          price: p.price,
          slug: p.slug,
          images: p.images,
          stock,
          quantity,
        },
      ];
    });
  };

  const updateQty = (id, qty) =>
    setCart((c) =>
      c.map((x) => {
        if (x.id !== id) return x;

        const requestedQty = Math.max(
          1,
          Number(qty) || 1
        );

        const stock =
          x.stock === null ||
          x.stock === undefined ||
          x.stock === ""
            ? null
            : Math.max(0, Number(x.stock) || 0);

        const quantity =
          stock !== null
            ? Math.min(requestedQty, stock)
            : requestedQty;

        return {
          ...x,
          quantity,
        };
      })
    );

  const removeItem = (id) =>
    setCart((c) => c.filter((x) => x.id !== id));

  const clear = () => setCart([]);

  const count = cart.reduce(
    (a, x) => a + x.quantity,
    0
  );

  const subtotal = cart.reduce(
    (a, x) => a + Number(x.price || 0) * x.quantity,
    0
  );

  return {
    cart,
    setCart,
    addItem,
    updateQty,
    removeItem,
    clear,
    count,
    subtotal,
  };
}

export function useWishlistLocal() {
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gmk-wish") || "[]"); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem("gmk-wish", JSON.stringify(wishlist)); }, [wishlist]);
  const toggle = (id) => setWishlist((w) => (w.includes(id) ? w.filter((x) => x !== id) : [...w, id]));
  const has = (id) => wishlist.includes(id);
  return { wishlist, toggle, has };
}
